import React, { useEffect, useState } from 'react';
import { GetCompanyWiseInventoryData } from '../../screen/distributor/action/util_action';
import {
  Box,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';
import InventoryCompanyCard from './components/InventoryCompanyCard';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import CustomButton from '../../v2/MUI_custom_component/Button/CustomButton';
import { InfoOutlined } from '@mui/icons-material';
import { trackAnalyticsEvent } from '../../utils/clevertap/clevertapUtils';
import * as Sentry from '@sentry/react';
import IllustrationWrapper from '../Illustrration/IllustrationWrapper';
import NoInventorySvg from '../Illustrration/components/NoInventorySvg';
import StickyEntity from '../Common/Entity/StickyEntity';
import { MODULE_PERMISSION_ENUM } from '../Sidebar/Enum';
import NoAccess from '../Common/NoAccess/NoAccess';
import { getSelectedEntityStatus } from '../Reducers/Entity/EntityReducer';
import { STATUS } from '../Common/Entity/Enums';

const Inventory = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyData, setSelectedCompanyData] = useState(null);
  const {
    selectedSupplierData,
    selectedSupplier: selectedCompany,
    selectedEntity: selectedCompanyEntity,
    selectedEntityInfo,
  } = useSelector(
    (state) => state.entityReducer
  );
  const hasAccess = selectedEntityInfo?.has_access;
  const showHistory = useSelector(state => state.entityReducer.show_history);
  useEffect(() => {
    if (selectedCompanyEntity && hasAccess !== false) {
      getCompanyWiseInventory(selectedCompanyEntity);
    } else {
      setCompanies([]);
      setSelectedCompanyData(null);
    }
  }, [selectedCompanyEntity, hasAccess]);
  const getCompanyWiseInventory = async (company_esi_id) => {

    dispatch({ type: 'RESET_BCM_ORDER' });
    dispatch({ type: 'RESET_FMCG_REDUCER' });
    setIsLoading(true);

    // Track inventory page load
    trackAnalyticsEvent('INVENTORY_PAGE_LOADED', {
      screen_name: 'inventory_main',
      action: 'page_load',
      company_esi_id: company_esi_id
    });

    const { error, data } = await GetCompanyWiseInventoryData(company_esi_id);
    setIsLoading(false);

    if (!error && data?.length > 0) {
      let index = data?.findIndex((ele) => ele.company_id == selectedCompany);
      if (index === -1) index = 0;
      const selected = data?.[index];
      setCompanies(data);
      setSelectedCompanyData(selected);
      dispatch({ type: 'SET_SELLER_ID', data: selected?.company_id });
      dispatch({ type: 'SET_COMPANY_ESI_ID', data: company_esi_id });
      trackAnalyticsEvent('INVENTORY_DATA_LOADED', {
        screen_name: 'inventory_main',
        companies_count: data.length,
        selected_company_id: selected?.company_id,
        selected_company_name: selected.company_name
      });
    } else {
      setCompanies([]);
      setSelectedCompanyData(null);
      trackAnalyticsEvent('INVENTORY_DATA_LOAD_FAILED', {
        screen_name: 'inventory_main',
        error_message: error || 'No data available'
      });
      if (error) {
        Sentry.captureMessage('GetCompanyWiseInventoryData failed', {
          level: 'warning',
          tags: { module: 'inventory', flow: 'inventory_main' },
          extra: { company_esi_id, error },
        });
      }
    }
  };
  const isEntityEnabled = selectedSupplierData?.entity_enabled;
  
  const marginTop = selectedEntityInfo?.status === STATUS.INACTIVE && selectedEntityInfo?.has_history === false
  return (
    <>
      <StickyEntity module_name = {MODULE_PERMISSION_ENUM.INVENTORY} />
      {hasAccess === false ?(
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '60vh',
          }}
        >
          <NoAccess />
        </Box>
      ) : (
        <Box sx={{ mt: {xs : !marginTop ? '90px' : 0, md: 0 }, p: 2, display: 'flex', flexDirection: 'column', gap: { xs:1, md:2 } }}>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12}>
              {isLoading ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height={300}
                >
                  <CircularProgress />
                </Box>
              ) : !selectedCompanyData ? (
                <IllustrationWrapper
                  illustration={<NoInventorySvg />}
                  title="No Inventory found"
                />
              ) : (
                <>
                  <InventoryCompanyCard
                    clickHandler={() => {
                      trackAnalyticsEvent('INVENTORY_VIEW_CLICKED', {
                        screen_name: 'inventory_main',
                        company_id: selectedCompany,
                        company_name: selectedCompanyData.company_name,
                        action: 'view_inventory_button_click',
                        navigation_target: 'company_inventory'
                      });
                      // navigate('/inventory/company-inventory', { state: { companyId: selectedCompany, company_esi_id: selectedCompanyEntity  } });
                    }}
                    logo={selectedCompanyData.company_logo}
                    name={selectedCompanyData.company_name}
                    date={selectedCompanyData.last_invoice_date || '-'}
                    unit={selectedCompanyData.total_quantity?.unit || '-'}
                    availableQty={
                      selectedCompanyData.allocable_quantity?.quantity_in_metric_ton
                    }
                    unverifiedQty={
                      selectedCompanyData.blocked_quantity?.quantity_in_metric_ton
                    }
                    badgeText={selectedCompanyData?.expiry_badge}
                    actionButton={
                      
                        <CustomButton
                          variant="filled"
                          color="primary"
                          label="View Inventory"
                          onClick={() => {
                            // Track View Inventory button click
                            trackAnalyticsEvent('INVENTORY_VIEW_CLICKED', {
                              screen_name: 'inventory_main',
                              company_id: selectedCompany,
                              company_name: selectedCompanyData.company_name,
                              action: 'view_inventory_action_button_click',
                              navigation_target: 'company_inventory'
                            });
                            navigate('/inventory/company-inventory', { state: { companyId: selectedCompany } });
                          }}
                          disabled={showHistory}
                        />
                    }
                  />
                  {selectedCompanyData.available_inventory && (
                  <>
                    <Box sx={{ my: 2 }}>
                      <Typography variant="h2" sx={{ mb: 1, color: '#333333' }}> Available Inventory</Typography>
                      <Grid container spacing={2}>
                        {selectedCompanyData.available_inventory?.map((item, index) => (
                            <Grid item xs={6}>
                                <Box key={index} sx={{ p: 2, borderRadius: 2, backgroundColor: '#fff'  }}>
                                  <Typography variant="h2" sx={{ mb: 1 }}>
                                    {item.month_label}
                                  </Typography>
                                  <Typography variant="body1">
                                    {item.available_quantity.quantity_in_metric_ton} {item.available_quantity.unit}
                                  </Typography>
                                </Box>
                            </Grid>
                          ))
                        }
                      </Grid>
                    </Box>
                  {selectedCompanyData?.expiry_info && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <InfoOutlined
                        fontSize='small'
                        sx={{
                          color: '#8C8C8C',
                          cursor: 'pointer',
                          mr: .5,
                        }}
                      />
                    <Typography variant='labelM' sx={{
                      color: '#8C8C8C',
                    }}>{selectedCompanyData?.expiry_info}</Typography>
                  </Box>)}
                  </>
                  )}
                  {selectedCompanyData.inventory_allocation && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="h2" sx={{ mb: 2, color: '#333333' }}>
                        Inventory Allocation
                      </Typography>
                      <Grid container spacing={2} >
                        {selectedCompanyData.inventory_allocation?.map((item, index) => (
                          <Grid item xs={12} key={index}>
                            <Box sx={{ 
                              p: 2,
                              borderRadius: 2, 
                              backgroundColor: '#fff' 
                            }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
                                <Typography variant="h2">{item.month_label}</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                                  <Typography variant="body1" fontWeight="bold">Total Sales:</Typography>
                                  <Typography variant="body1" sx={{ ml:1 }}>{item.total_sales.quantity_in_metric_ton} {item.total_sales.unit}</Typography>
                                </Box>
                              </Box>

                              {/* Allocation details */}
                              <Grid container spacing={1} alignItems={'center'}>
                                <Grid item xs={4} sm={4} >
                                  <Box sx={{
                                      borderRight:
                                      item.total_sales.quantity_in_metric_ton ? '2px solid #A3A3A380'
                                        : 'none',
                                    }}>
                                    <Typography variant="labelM" sx={{ color: '#666666', mb: 0.2}}>Onward Allocation</Typography>
                                    <Typography variant="body1" fontWeight="600">
                                      {item.onward_allocation.quantity_in_metric_ton} {item.onward_allocation.unit}
                                    </Typography>
                                  </Box>
                                </Grid>

                                <Grid item xs={4} sm={4}>
                                  <Box sx={{
                                    borderRight:
                                    item.total_sales.quantity_in_metric_ton ? '2px solid #A3A3A380'
                                      : 'none',
                                  }}>
                                  <Typography variant="labelM" sx={{ color: '#666666', mb: 0.2}}>Allocation %</Typography>
                                  <Typography variant="body1" fontWeight="600">
                                    {item.allocation_percentage}
                                  </Typography>
                                  </Box>
                                </Grid>

                                <Grid item xs={4} sm={4}>
                                  <Box>
                                    <Typography variant="labelM" sx={{ color: '#666666',mb: 0.2}}>Expired Qty.</Typography>
                                    <Typography variant="body1" fontWeight="600">
                                      {item.expired_quantity.quantity_in_metric_ton} {item.expired_quantity.unit}
                                    </Typography>
                                  </Box>
                                </Grid>
                              </Grid>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </Box>
      )}
    </>
  );
};

export default Inventory;
